// @need-translate
import {useRouter} from 'next/router';
import {ReactNode, useCallback} from 'react';
import {twMerge} from 'tailwind-merge';
import {AGE_GRADE_TO_NUMBER_MAP} from '../../../common/constant';
import {TrackPageViewContainer} from '../../../common/containers/TrackPageViewContainer';
import {getShouldSelfCertToAccessContent} from '../../../common/getShouldSelfCertToAccessContent';
import {t, tt} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import useTypedDispatch from '../../../common/useTypedDispatch';
import {ButtonV2} from '../../../design-system/atoms/ButtonV2/ButtonV2';
import {
  ButtonV2Size,
  ButtonV2Variant,
} from '../../../design-system/atoms/ButtonV2/ButtonV2.enum';
import {Loading} from '../../../design-system/atoms/Loading/Loading';
import {StaticImage} from '../../../design-system/atoms/StaticImage/StaticImage';
import {StaticImageType} from '../../../design-system/atoms/StaticImage/StaticImageType.enum';
import {Text} from '../../../design-system/atoms/Text/Text';
import {ErrorArea} from '../../../design-system/organisms/ErrorArea/ErrorArea';
import {setFooter} from '../../../footer/FooterSlice';
import {AgeGrade} from '../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../header/constants';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../reactQuery/constant';
import {
  NEED_PARENT_SELF_CERT_AGE,
  SELF_CERT_REQUEST_TO_TEXT_MAP,
} from '../../../selfCert/constants';
import {SelfCertRequestType} from '../../../selfCert/SelfCertRequestType.enum';
import {processSelfCertRequest} from '../../../user/processSelfCertRequest';
import {useLogin} from '../../../user/useLogin.hook';
import {useSinglePreviewQuery} from '../../../wrapper';
import {
  useContentHomeOverviewQuery,
  useSimpleSeriesSingleInfoQuery,
} from '../../../wrapper/contentWrapper';
import {Under14AgeHasParentCertificatedCheckerHeadless} from './Under14AgeHasParentCertificatedCheckerHeadless';

export interface ContentAgeAuthLimitCheckerHeadlessProps {
  seriesId: number;
  productId?: number;
  /**
   * 기본적으론 overview, viewerData 의 타이틀을 보여주지만 필요한 경우 prop 으로 넘어온 경우를 일순위로 보여줍니다
   */
  contentTitle?: string;
  /**
   * PV 를 위해 남김
   */
  location: 'setoverview' | 'contenthome' | 'viewer';
  ageGrade?: AgeGrade;
  children?: ReactNode;
}

export function ContentAgeAuthLimitCheckerHeadless({
  seriesId,
  productId,
  contentTitle,
  location,
  ageGrade: _ageGrade,
  children,
}: ContentAgeAuthLimitCheckerHeadlessProps) {
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
    ?.contentHomeOverview;

  const singlePreviewResult = useSinglePreviewQuery(
    // @ts-ignore
    {productId},
    {enabled: !!productId}
  );
  const singlePreview = singlePreviewResult.data;
  const title =
    contentTitle ||
    (singlePreview && singlePreview.singlePreview?.title) ||
    data?.content?.title;
  const dispatch = useTypedDispatch();

  const singleSimpleResult = useSimpleSeriesSingleInfoQuery({
    seriesId,
    productId,
  });
  const singleSimple = singleSimpleResult.data;

  const ageGrade =
    _ageGrade ||
    (productId && singleSimple?.simpleSeriesSingleInfo?.singleAgeGrade) ||
    data?.content?.ageGrade;

  const router = useRouter();
  const {isLogin, doLogin, user} = useLogin();
  const onClickLogin = useCallback(() => {
    doLogin();
  }, [doLogin]);
  const onClickGoToHome = useCallback(() => {
    router.replace('/');
  }, [router]);
  const {isMobile, isWebView} = requestContext.requestInfo.display;

  const locationName = location === 'viewer' ? '뷰어' : '콘텐츠홈';

  const hideFooter = useCallback(() => {
    dispatch(
      setFooter({
        mobile: {
          isExposed: false,
        },
      })
    );
  }, [dispatch]);

  if (!ageGrade) {
    return children && !isWebView ? (
      <div className="flex-center-full-auto">
        <Loading />
      </div>
    ) : null;
  }

  if (AGE_GRADE_TO_NUMBER_MAP[ageGrade] >= 19) {
    if (!isLogin) {
      location === 'contenthome' && isMobile && hideFooter();
      return (
        <div className="flex-center-full-auto">
          <TrackPageViewContainer
            key="비로그인_화면"
            trackObj={{
              section: locationName,
              page: `${locationName}_${AGE_GRADE_TO_NUMBER_MAP[ageGrade]}세_비로그인`,
              actionType: 'Pageview',
              actionName: `${locationName}_${AGE_GRADE_TO_NUMBER_MAP[ageGrade]}세_비로그인_화면`,
              page_meta: {
                ...(location === 'viewer'
                  ? {
                      id: String(productId),
                      type: 'productId',
                    }
                  : {
                      id: String(seriesId),
                      type: 'seriesId',
                    }),
                series_id: String(seriesId),
              },
              customProps: {
                user_action: 'pv',
              },
            }}
          />
          <ErrorArea
            isMobile={requestContext.requestInfo.display.isMobile}
            {...putTiaraObjectProps({
              click: {layer1: '본인확인_비로그인'},
            })}
          >
            <ErrorArea.Title>{title}</ErrorArea.Title>
            <ErrorArea.MainText>
              {t('viewer_adult_verification')}
            </ErrorArea.MainText>
            <ErrorArea.LabelButton
              onClick={e => LOG_CLICK_EVENT('로그인_클릭', e, onClickLogin)}
            >
              {t('common_login_button')}
            </ErrorArea.LabelButton>
          </ErrorArea>
        </div>
      );
    } else if (
      !user?.age ||
      getShouldSelfCertToAccessContent({
        ageGrade,
        userAge: user.age,
        userAuthenticatedAt: user.authenticatedAt,
      })
    ) {
      location === 'contenthome' && isMobile && hideFooter();
      return (
        <div className={twMerge('flex-center-full-auto', isMobile && 'block')}>
          <TrackPageViewContainer
            key="본인확인안내_화면"
            trackObj={{
              section: locationName,
              page: `${locationName}_${AGE_GRADE_TO_NUMBER_MAP[ageGrade]}세_본인확인안내`,
              actionType: 'Pageview',
              actionName: `${locationName}_${AGE_GRADE_TO_NUMBER_MAP[ageGrade]}세_본인확인안내_화면`,
              page_meta: {
                ...(location === 'viewer'
                  ? {
                      id: String(productId),
                      type: 'productId',
                    }
                  : {
                      id: String(seriesId),
                      type: 'seriesId',
                    }),
                series_id: String(seriesId),
              },
              customProps: {
                user_action: 'pv',
              },
            }}
          />
          <div
            className={twMerge(
              'flex flex-col w-full',
              isMobile
                ? 'h-screen px-15pxr'
                : 'flex-1 justify-center items-center text-center'
            )}
            style={{
              paddingTop: `${isMobile ? MOBILE_HEADER_HEIGHT_PX + 20 : 20}px`,
            }}
          >
            {isMobile && (
              <div
                className="fixed top-0 w-full bg-theme-solid-0"
                style={{
                  height: `${MOBILE_HEADER_HEIGHT_PX}px`,
                }}
              />
            )}
            <div
              className={twMerge(
                'flex flex-col py-24pxr px-18pxr',
                !isMobile && 'w-[480px]',
                isMobile && 'w-full'
              )}
            >
              <div
                className={twMerge(
                  'flex flex-col',
                  isMobile && 'gap-12pxr',
                  !isMobile && 'justify-center items-center px-50pxr'
                )}
              >
                <StaticImage
                  imageType={StaticImageType.Age19Static}
                  className={twMerge('h-90pxr w-90pxr')}
                />
                <Text
                  className={twMerge(
                    'font-medium1-bold text-theme-solid-100',
                    !isMobile && 'font-large3-bold mt-24pxr'
                  )}
                  dangerouslySetInnerHTML={{
                    __html: tt(
                      SELF_CERT_REQUEST_TO_TEXT_MAP[SelfCertRequestType.Age19]
                        .main
                    ),
                  }}
                />
                <Text
                  className={twMerge(
                    'font-small1 text-theme-solid-60',
                    !isMobile && 'mt-12pxr'
                  )}
                  dangerouslySetInnerHTML={{
                    __html: tt(
                      SELF_CERT_REQUEST_TO_TEXT_MAP[SelfCertRequestType.Age19]
                        .desc
                    ),
                  }}
                />
              </div>
            </div>
            <div
              className={twMerge(
                'bottom-0',
                isMobile && 'fixed inset-x-0 p-11pxr'
              )}
            >
              <div
                className={twMerge(
                  'flex',
                  !isMobile && 'mt-12pxr w-320pxr flex-col-reverse gap-y-10px',
                  isMobile && 'mt-20pxr space-x-8pxr'
                )}
                {...putTiaraObjectProps({
                  click: {layer1: '본인확인'},
                })}
              >
                <ButtonV2
                  variant={ButtonV2Variant.Line}
                  size={ButtonV2Size.Large}
                  onClick={e => {
                    LOG_CLICK_EVENT('취소_클릭', e, () => router.push('/'));
                  }}
                >
                  {t('common_cancel_button')}
                </ButtonV2>
                <ButtonV2
                  variant={ButtonV2Variant.Primary}
                  size={ButtonV2Size.Large}
                  onClick={e => {
                    LOG_CLICK_EVENT('본인확인하기_클릭', e, () => {
                      processSelfCertRequest({
                        selfCertRequestParams: {
                          type: SelfCertRequestType.Age19,
                        },
                      });
                    });
                  }}
                >
                  {t('mainhome_age_verification_popup_button')}
                </ButtonV2>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (
      user?.age < NEED_PARENT_SELF_CERT_AGE &&
      !user?.isParentCertificated
    ) {
      location === 'contenthome' && isMobile && hideFooter();
      return <Under14AgeHasParentCertificatedCheckerHeadless />;
    } else if (user?.age < AGE_GRADE_TO_NUMBER_MAP[ageGrade]) {
      location === 'contenthome' && isMobile && hideFooter();
      return (
        <div className="flex-center-full-auto">
          <TrackPageViewContainer
            key="진입불가_화면"
            trackObj={{
              section: locationName,
              page: `${locationName}_${AGE_GRADE_TO_NUMBER_MAP[ageGrade]}세_진입불가`,
              actionType: 'Pageview',
              actionName: `${locationName}_${AGE_GRADE_TO_NUMBER_MAP[ageGrade]}세_진입불가_화면`,
              page_meta: {
                ...(location === 'viewer'
                  ? {
                      id: String(productId),
                      type: 'productId',
                    }
                  : {
                      id: String(seriesId),
                      type: 'seriesId',
                    }),
                series_id: String(seriesId),
              },
              customProps: {
                user_action: 'pv',
              },
            }}
          />
          <ErrorArea
            isMobile={requestContext.requestInfo.display.isMobile}
            {...putTiaraObjectProps({
              click: {layer1: '본인확인_진입불가'},
              eventMeta: {id: 'kakaopage://open/main', type: 'scheme'},
            })}
          >
            <ErrorArea.MainText>
              {t('verify_banned_toast', {
                values: {
                  value: AGE_GRADE_TO_NUMBER_MAP[ageGrade],
                },
              })}
            </ErrorArea.MainText>
            <ErrorArea.LabelButton
              onClick={e =>
                LOG_CLICK_EVENT('홈으로가기_클릭', e, onClickGoToHome)
              }
            >
              {t('contenthome_home_button')}
            </ErrorArea.LabelButton>
          </ErrorArea>
        </div>
      );
    }
  }

  if (!children) {
    return null;
  }

  return <>{children}</>;
}
